import React, { useRef } from 'react';
import styles from './Constellation.module.css';
import TutorialProgressService, { ConstellationProgress } from './TutorialProgressService';
import { getTutorialTitle } from './TutorialTitles';
import PulsingArrow from './PulsingArrow';
import Beacon from './Beacon';

export interface StarPosition {
  x: number; // percentage from left of constellation
  y: number; // percentage from top of constellation
}

export interface ConstellationData {
  name: string;
  image: string;
  position: {
    x: number; // percentage from left of screen
    y: number; // percentage from top of screen
    scale: number; // size multiplier
    rotation: number; // rotation in degrees
  };
  // Per-constellation tweak for the railroad framing zoom. Default is 1.0;
  // values <1 zoom out, >1 zoom in. Use when an illustration fills its
  // 100x100 source frame differently than peers and the uniform formula
  // over- or under-zooms it.
  railroadZoomFactor?: number;
  stars: StarPosition[];
}

interface ConstellationProps {
  constellation: ConstellationData;
  onClick?: () => void;
  onStarClick?: (constellationName: string, starIndex: number) => void;
  nextTutorial?: { constellation: string; starIndex: number } | null;
}

function Constellation({ constellation, onClick, onStarClick, nextTutorial }: ConstellationProps) {
  const { name, image, position, stars } = constellation;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Get progress from service
  const progress = TutorialProgressService.getConstellationProgress(name);
  if (!progress || !progress.isUnlocked) {
    return null; // Don't render locked constellations
  }

  // Create audio context for space sound
  const playHoverSound = () => {
    try {
 // Debug log
      // Create audio context with Web Audio API for deep, mysterious space sound
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Resume audio context if suspended (required by some browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      // Create oscillators for deep, cinematic humming effect
      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const oscillator3 = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const filterNode = audioContext.createBiquadFilter();
      
      // Set up deeper, more mysterious sound parameters
      oscillator1.type = 'triangle'; // Smoother than sawtooth
      oscillator2.type = 'sine';     // Pure tone for depth
      oscillator3.type = 'sine';     // Subtle harmonic
      
      // Much lower frequencies for deeper, more mysterious sound
      oscillator1.frequency.setValueAtTime(65, audioContext.currentTime);  // Low fundamental
      oscillator2.frequency.setValueAtTime(98, audioContext.currentTime);  // Perfect fifth above
      oscillator3.frequency.setValueAtTime(130, audioContext.currentTime); // Octave above fundamental
      
      // Slower, more gradual frequency modulation for cinematic effect
      oscillator1.frequency.linearRampToValueAtTime(75, audioContext.currentTime + 0.6);
      oscillator2.frequency.linearRampToValueAtTime(110, audioContext.currentTime + 0.6);
      oscillator3.frequency.linearRampToValueAtTime(145, audioContext.currentTime + 0.6);
      
      // Add subtle vibrato with very low frequency LFO
      const lfo = audioContext.createOscillator();
      const lfoGain = audioContext.createGain();
      lfo.type = 'sine';
      lfo.frequency.setValueAtTime(2, audioContext.currentTime); // 2Hz vibrato
      lfoGain.gain.setValueAtTime(3, audioContext.currentTime); // Subtle modulation depth
      lfo.connect(lfoGain);
      lfoGain.connect(oscillator1.frequency);
      
      // Low-pass filter for warmer, more atmospheric tone
      filterNode.type = 'lowpass';
      filterNode.frequency.setValueAtTime(400, audioContext.currentTime);
      filterNode.Q.setValueAtTime(2, audioContext.currentTime);
      
      // Longer, more cinematic volume envelope (quieter)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.015, audioContext.currentTime + 0.05);  // Slower attack, half volume
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.8); // Much longer sustain
      
      // Connect the audio graph
      oscillator1.connect(filterNode);
      oscillator2.connect(filterNode);
      oscillator3.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Start all oscillators
      lfo.start(audioContext.currentTime);
      oscillator1.start(audioContext.currentTime);
      oscillator2.start(audioContext.currentTime);
      oscillator3.start(audioContext.currentTime);
      
      // Stop after longer duration for more cinematic effect
      lfo.stop(audioContext.currentTime + 0.8);
      oscillator1.stop(audioContext.currentTime + 0.8);
      oscillator2.stop(audioContext.currentTime + 0.8);
      oscillator3.stop(audioContext.currentTime + 0.8);
      
      // Clean up after sound finishes
      setTimeout(() => {
        audioContext.close();
      }, 900);
    } catch (error) {
      // Handle audio context errors with more detailed logging
    }
  };
  
  const opacity = TutorialProgressService.getConstellationOpacity(name);
  const isFullyCompleted = progress.completedChallenges >= progress.totalChallenges;
  
  return (
    <div 
      className={`${styles.constellation} ${isFullyCompleted ? styles.completed : ''}`}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: `scale(${position.scale}) rotate(${position.rotation}deg)`,
      }}
      onClick={onClick}
    >
      {/* Constellation outline image */}
      <img 
        src={`/images/${image}`}
        alt={name}
        className={styles.constellationImage}
        style={{ opacity }} // Dynamic opacity based on progress
      />
      
      {/* Stars positioned on the constellation using new star images */}
      {stars.map((star, index) => {
        const challenge = progress.challenges[index];
        
        // Handle case where we have more stars than challenges (due to cached data)
        let starImage = 'starr5.png'; // Default incomplete
        let isCompleted = false;
        
        if (challenge) {
          isCompleted = challenge.completed;
          if (challenge.completed) {
            // New 3-state system: Incomplete -> Pass -> Super
            switch (challenge.completionState) {
              case 'Super':
                starImage = 'starr8.png';
                break;
              case 'Pass':
                starImage = 'starr7.png';
                break;
              default:
                starImage = 'starr5.png';
            }
          }
        }

        const tutorialTitle = getTutorialTitle(name, index);

        const handleStarClick = (e: React.MouseEvent) => {
          e.stopPropagation(); // Prevent triggering constellation click
          onStarClick?.(name, index);
        };

        const handleStarMouseEnter = () => {
          playHoverSound();
        };

        return (
          <React.Fragment key={index}>
            <img
              src={`/images/${starImage}`}
              alt={`Star ${index + 1}`}
              title={tutorialTitle}
              className={`${styles.star} ${isCompleted ? styles.activeStar : ''}`}
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
              }}
              onClick={handleStarClick}
              onMouseEnter={handleStarMouseEnter}
            />
            
            {/* Beacon for next incomplete tutorial */}
            {nextTutorial &&
             nextTutorial.constellation === name &&
             nextTutorial.starIndex === index && (
              <Beacon
                x={star.x}
                y={star.y}
                visible={true}
              />
            )}
          </React.Fragment>
        );
      })}
      
      {/* Pulsing arrow for Bear1 tutorial when no tutorials completed */}
      {name === 'Bear' && stars.length > 0 && !TutorialProgressService.hasAnyCompletedTutorials() && (
        <PulsingArrow
          targetX={stars[0].x -8}
          targetY={stars[0].y + 15}
          visible={true}
          onClick={() => onStarClick?.(name, 0)}
        />
      )}
      
      {/* Constellation name (appears on hover or when completed) */}
      <div className={styles.constellationName}>
        {name}
        <div className={styles.progress}>
          {progress.completedChallenges}/{progress.totalChallenges}
          {progress.superChallenges > 0 && (
            <span className={styles.specialComplete}> ⭐</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default Constellation; 